import is from '@sindresorhus/is';
import { type ChangeEvent, createContext, type KeyboardEvent, useContext } from 'react';

import type { CreateJsaEntitiesMessageContent, RegenerateMessageContent } from '../data-contract/chat';
import type { ChatContext } from './chat-context-types';
import type { ChatConversation } from './chat-conversation';
import type { SelectedKnowledgeBaseDocument } from './components/KnowledgeBaseSelectDialog';
import type { PendingAttachment } from './components/PendingAttachmentList';
import type { UseChatTranscriptionReturn } from './hooks/use-chat-transcription';

export type SendMessageOptions =
    | {
          readonly messageType?: 'text';
      }
    | {
          readonly content: RegenerateMessageContent;
          readonly messageType: 'regenerate';
      }
    | {
          readonly content: CreateJsaEntitiesMessageContent;
          readonly messageType: 'create-jsa-entities';
      };

export type ChatComposerCallbacks = {
    /** Adds picked documents to the pending attachments; a re-picked file name replaces the old entry. */
    readonly addAttachmentFiles: (files: readonly File[]) => void;
    /** Adds knowledge base documents to the pending attachments by reference — no upload happens for them. */
    readonly addKnowledgeBaseAttachments: (documents: readonly SelectedKnowledgeBaseDocument[]) => void;
    readonly close: () => void;
    /** Returns the active thread id, creating a thread first when none is selected yet. */
    readonly ensureThreadId: () => Promise<string | null>;
    /** Reveals the collapsed pill's topSlot without expanding the full chat. */
    readonly expandCollapsedComposer: () => void;
    /** Replaces the draft with the given text without sending it. */
    readonly fillDraft: (text: string) => void;
    readonly handleDraftChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    readonly handleKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
    readonly hasComposerAppeared: boolean;
    readonly isComposerEnabled: boolean;
    readonly isComposerReady: boolean;
    readonly isFloatingComposerVisible: boolean;
    readonly markComposerAppeared: () => void;
    readonly open: () => void;
    readonly registerChatContext: (tileId: string, context: ChatContext) => void;
    readonly removeAttachment: (fileName: string) => void;
    readonly sendMessage: (text: string, options?: SendMessageOptions) => void;
    readonly toggleAttachmentKnowledgeBase: (fileName: string, addToKnowledgeBase: boolean) => void;
    readonly setCollapsedComposerOpen: (open: boolean) => void;
    readonly submit: () => void;
    readonly unregisterChatContext: (tileId: string) => void;
};

export type ChatComposerActive = {
    readonly activeChatContext: ChatContext | null;
    readonly activeChatContexts: readonly ChatContext[];
    /** Precedence: chat dock (if expanded) > right-most open sidepanel > main tile. */
    readonly activeTileId: string;
    /** Same as activeTileId but ignores chat-expanded short-circuit. */
    readonly anchorTileId: string;
    /** Increments every time a consumer requests the collapsed pill to expand. */
    readonly collapsedComposerExpandSignal: number;
    readonly isCollapsedComposerOpen: boolean;
    readonly isExpanded: boolean;
    /** True when the surface is left of its rightmost rest position (chat expanded). Drives the chat card lift. */
    readonly isChatScrolled: boolean;
    /** True when a tile has crossed the inter-tile gap and is under the (sticky) chat. Drives tile recede. */
    readonly isContentUnderChat: boolean;
    /** Preferred single-context fallback for UI that can only render one context at a time. */
    readonly resolvedChatContext: ChatContext | null;
    /** Context that drives the composer prompt and idle badge slots. */
    readonly promptChatContext: ChatContext | null;
};

export type ChatComposerInput = {
    /** Documents picked for the next message, uploaded when it is submitted. */
    readonly attachments: readonly PendingAttachment[];
    readonly draft: string;
    readonly transcription: UseChatTranscriptionReturn;
};

export const ChatComposerCallbacksContext = createContext<ChatComposerCallbacks | null>(null);
export const ChatComposerActiveContext = createContext<ChatComposerActive | null>(null);
export const ChatComposerInputContext = createContext<ChatComposerInput | null>(null);
export const ChatComposerConversationContext = createContext<ChatConversation | null>(null);

export const useChatComposerCallbacks = (): ChatComposerCallbacks => {
    const value = useContext(ChatComposerCallbacksContext);
    if (is.null(value)) {
        throw new Error('useChatComposerCallbacks must be used within a ChatComposerProvider');
    }
    return value;
};

export const useChatComposerActive = (): ChatComposerActive => {
    const value = useContext(ChatComposerActiveContext);
    if (is.null(value)) {
        throw new Error('useChatComposerActive must be used within a ChatComposerProvider');
    }
    return value;
};

export const useChatComposerInput = (): ChatComposerInput => {
    const value = useContext(ChatComposerInputContext);
    if (is.null(value)) {
        throw new Error('useChatComposerInput must be used within a ChatComposerProvider');
    }
    return value;
};

export const useChatComposerConversation = (): ChatConversation => {
    const value = useContext(ChatComposerConversationContext);
    if (is.null(value)) {
        throw new Error('useChatComposerConversation must be used within a ChatComposerProvider');
    }
    return value;
};
